import type { Channel, SocialSet } from './types'

/** One row in the "Posting as" menu: a saved set, or a channel in no set. */
export interface SetOption {
  id: string
  name: string
  channels: Channel[]
  set: SocialSet | null
}

export const channelOptionId = (channelId: string) => `channel:${channelId}`

/** Saved sets first (members resolved, disconnected ones dropped), then every
 * channel that is in no set as its own row, so each account is reachable. */
export function setOptions(sets: SocialSet[], channels: Channel[]): SetOption[] {
  const byId = new Map(channels.map((c) => [c.id, c]))
  const grouped = new Set<string>()
  const saved = sets.map((set) => {
    const members = set.channelIds.map((id) => byId.get(id)).filter((c): c is Channel => Boolean(c))
    for (const c of members) grouped.add(c.id)
    return { id: set.id, name: set.name, channels: members, set }
  })
  const loose = channels
    .filter((c) => !grouped.has(c.id))
    .map((c) => ({ id: channelOptionId(c.id), name: c.name, channels: [c], set: null }))
  return [...saved, ...loose]
}

export function activeSetOption(options: SetOption[], activeId: string | null): SetOption | null {
  return options.find((o) => o.id === activeId) ?? options[0] ?? null
}

/** The saved set whose members are exactly `channelIds`, if any. */
export function matchingSet(sets: SocialSet[], channelIds: string[]): SocialSet | null {
  const want = new Set(channelIds)
  if (want.size === 0) return null
  return sets.find((s) => s.channelIds.length === want.size && s.channelIds.every((id) => want.has(id))) ?? null
}
